import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Divider, IconButton, InputAdornment, InputBase, Menu, MenuItem } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { globalHistory } from '@reach/router';
import { Link } from 'gatsby';
import * as React from 'react';
import { cameraIcon, logo } from '../assets/icons';
import { AuthUserContext } from '../contexts/AuthUserContext';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { NovadeLiteUrlContext } from '../contexts/NovadeLiteUrlContext';
import { useStrapiHeader } from '../hooks/useStrapiHeader';
import { globalColors } from '../styles/globalColorVariables';
import {
  AccountMenuStyles,
  HeaderContainer,
  InactiveLink,
  LeftHeader,
  LinkTypography,
  SearchContainer,
  SearchInput,
  StyledAvatar
} from '../styles/Header';
import { AuthUserContextType, LocationLanguageContextType, LogInResponse, NovadeLiteUrlContextType } from '../typings';
import { clearAndGoToBrowse, clickToSearch, enterToSearch, getInitials } from '../utils/common';
import { Languages } from '../utils/getAllSupportedLanguages';
import { setGTMEvent } from '../utils/setGAEvent';
import NvdButton from './Button';
import LoginDialog from './LoginDialog';

interface HeaderProps {
  isHomePage: boolean;
}

const Header: React.FC<HeaderProps> = ({ isHomePage }) => {
  const [open, setOpen] = React.useState(false);
  const [searchFieldValue, setSearchFieldValue] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const authUser = React.useContext<AuthUserContextType>(AuthUserContext);
  const novadeLiteAPI = React.useContext<NovadeLiteUrlContextType>(NovadeLiteUrlContext);
  const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
  const language: string = locationLanguage?.language ?? Languages.English;

  const headerContent = useStrapiHeader(language);
  const databasesMap = novadeLiteAPI ? authUser?.databasesMap[novadeLiteAPI.novadeLiteUrl] || {} : {};
  const { token } = Object.values(databasesMap)[0] ?? '';

  const login = () => {
    setOpen(true);
  };

  async function submitLogoutRequest() {
    const NOVADE_LITE_LOGOUT_URL = `${novadeLiteAPI?.novadeLiteUrl}/logout`;
    const response = await fetch(NOVADE_LITE_LOGOUT_URL, {
      method: 'POST',
      headers: token ? { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : undefined,
      credentials: 'include',
      body: ''
    });
    if (!response.ok) {
      // Error logging out
    } else {
      setGTMEvent('logout', 'logout', 'Logout from Novade Template App');
    }
  }

  const logout = () => {
    setAnchorEl(null);
    authUser?.logout();
    novadeLiteAPI?.updateNovadeLiteUrl(process.env.GATSBY_NOVADE_LITE_LOGIN_URL_SEA!);
    submitLogoutRequest();
    localStorage.clear();
    setOpen(false);
  };

  const handleDialogClose = (value: LogInResponse | undefined) => {
    if (value) {
      authUser?.updateUser({
        email: value.email,
        displayName: value.userDisplayName
      });
      authUser?.updateDatabasesMap(value.databases);
    }
    setOpen(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFieldValue(event.target.value);
  };

  const search = (event: React.KeyboardEvent) => {
    enterToSearch(event, searchFieldValue);
  };

  const handleSearchClick = () => {
    clickToSearch(searchFieldValue);
  };

  const handleClearClick = () => {
    clearAndGoToBrowse();
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    const unlisten = globalHistory.listen(({ location }) => {
      const queryParams = new URLSearchParams(location.search);
      const keyword = queryParams.get('keywords') || '';
      const isSearchPage = location.pathname.includes('/search');

      if (isSearchPage) setSearchFieldValue(keyword);
      else setSearchFieldValue('');
    });
    return unlisten;
  }, []);

  const matchesDesktop = useMediaQuery('(min-width:1025px)');
  if (!headerContent) return null;

  const pages = {
    home: { name: headerContent.home, link: '/' },
    templates: { name: headerContent.templates, link: '/browse' },
    dashboards: { name: headerContent.dashboards, link: '/dashboards' }
  };
  return (
    <AppBar position='sticky' color='transparent'>
      <Toolbar style={{ backgroundColor: 'white' }}>
        <Container maxWidth='xl'>
          <HeaderContainer>
            <LeftHeader>
              <Link to='/' style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <img src={logo} alt='Novade Logo' width={48} />
                  <div>
                    <Typography style={{ fontWeight: 600, lineHeight: 1.3, fontSize: '18px' }}>Novade</Typography>
                    <Typography>{headerContent.logoLabel}</Typography>
                  </div>
                </div>
              </Link>
              <div style={{ display: 'flex', alignItems: 'center', margin: '0 1rem' }}>
                {Object.entries(pages).map(([key, page]) => (
                  <LinkTypography variant='body2' key={key}>
                    <InactiveLink href={page.link}>{page.name}</InactiveLink>
                  </LinkTypography>
                ))}
              </div>
              {!isHomePage && (
                <SearchContainer>
                  <SearchInput elevation={0}>
                    <InputBase
                      onChange={handleSearchChange}
                      fullWidth
                      onKeyDown={search}
                      value={searchFieldValue}
                      placeholder={headerContent.search}
                      endAdornment={
                        <InputAdornment position='end'>
                          {searchFieldValue && (
                            <IconButton aria-label='clear' edge='end' onClick={handleClearClick}>
                              <CancelIcon />
                            </IconButton>
                          )}
                          <IconButton aria-label='search' edge='end' onClick={handleSearchClick}>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      }
                      inputProps={{ 'aria-label': `${headerContent.search}` }}
                    />
                  </SearchInput>
                </SearchContainer>
              )}
            </LeftHeader>
            {authUser?.user ? (
              <div>
                <Button
                  aria-controls='user menu'
                  size='medium'
                  aria-haspopup='true'
                  onClick={handleMenuClick}
                  sx={AccountMenuStyles}
                  endIcon={<ExpandMoreIcon color='disabled' />}
                >
                  {authUser.user.displayName ? (
                    <StyledAvatar>{getInitials(authUser.user.displayName)}</StyledAvatar>
                  ) : (
                    <img src={cameraIcon} alt='User' width={48} />
                  )}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                  <div style={{ padding: '0 1rem 6px 1rem', minWidth: '15rem' }}>
                    <Typography variant='body2'>{authUser.user.displayName}</Typography>
                    <Typography variant='caption'>{authUser.user.email}</Typography>
                  </div>
                  <Divider variant='middle' />
                  <MenuItem onClick={logout}>{headerContent.logout}</MenuItem>
                </Menu>
              </div>
            ) : (
              <div>
                <NvdButton
                  variant='outlined'
                  onClick={login}
                  label={headerContent.login ?? 'Login'}
                  sx={{
                    backgroundColor: globalColors.whiteColor,
                    color: globalColors.primaryColor,
                    marginLeft: '1rem'
                  }}
                />
              </div>
            )}
            {matchesDesktop && !authUser?.user && (
              <NvdButton
                href={headerContent.signUpUrl}
                label={headerContent.signUp ?? 'Sign Up'}
                sx={{ backgroundColor: globalColors.primaryColor, color: globalColors.whiteColor, marginLeft: '1rem' }}
              />
            )}
            <LoginDialog open={open} onDialogClose={handleDialogClose} />
          </HeaderContainer>
        </Container>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
